import { AxiosError } from 'axios';
import { FORM_ERROR } from 'final-form';
import { unflatten } from 'flat';
import { AppSiteDto, ReplacementTokenFields } from '../types';
import AppState from 'AppState';
import { TokenAvailability } from 'components/shared/AvailableReplacementTokens';
import merge from 'lodash.merge';

/**
 * Represents the source of validation errors returned by `handleAxiosErrorWithValidation`. These act as bitwise flags,
 * so test these individually.
 */
export enum ValidationErrorSource {
	/** No validation errors at all, so no source. */
	None = 0,

	/** Validation error in the edit form itself. */
	EditForm = 1,

	/** Validation error in only the redirect URLs. */
	RedirectModal = 2,
}

export const getFormattedPageTitle = (subTitle: string = ''): string => {
	// load from config?
	const siteName = 'ROC';

	if (!subTitle || !subTitle.trim()) {
		return siteName;
	}

	return `${subTitle.trim()} - ${siteName}`;
};

/**
 * Promise version of setTimeout.
 * @param ms The number of milliseconds to wait for.
 */
export const timeout = (ms) => {
	return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Allows you to await on a timeout, useful for avoiding setTimeout callbacks and for troubleshooting.
 * @param ms The number of milliseconds to wait for.
 */
export const sleep = async (ms: number = 0) => {
	await timeout(ms);
};

/**
 * Handles Axios error for React Final Form. If the error is a validation error, the error is transformed into React
 * Final Form field validation object. If the error is a generic error, the error is rethrown instead.
 *
 * @param error - The Axios Error object to handle errors for.
 * @param errorMessage - The form error message to provide back to React Final Form.
 * @param validationErrorCallback - Optional callback that is triggered right before the validation object is returned
 * to Final Form, so that you can run logic that should occur during validation errors.
 * @param genericErrorCallback - Optional callback that is triggered right before the generic error is rethrown, so that
 * you can run logic that should occur during generic errors.
 */
export const handleAxiosErrorWithValidation = (
	error: AxiosError,
	errorMessage: string | null = 'An error occurred while saving.',
	validationErrorCallback?: () => void,
	genericErrorCallback?: () => void,
): { [index: string]: string } => {
	if (error && error.response && error.response.status === 400 && error.response.data) {
		let globalErrors;

		const errors = error && error.response && error.response.data;

		// by default, we display client handleable exception messages at the top of the form.
		// this behavior can be overridden per specific component if custom handling is needed.
		// eg: a client handleable exception may involve asking a user for further approval to "force" deletes
		if (errors.__isClientHandleable) {
			return {
				[FORM_ERROR]: errors.message,
			};
		}

		const errorsWithModifiedArrayIndex = Object.keys(errors).reduce((acc, value) => {
			if (value === '') {
				// in case we are getting back a non field specific error store it in the global errors
				globalErrors = errors[value];
			} else {
				const newKey = value.replace(/\[([0-9]+)\]/g, '.$1');
				acc[newKey] = errors[value];
			}

			return acc;
		}, {});

		const newValidationState = { ...unflatten(errorsWithModifiedArrayIndex) };

		console.debug('newValidationState:', newValidationState);

		if (validationErrorCallback) {
			validationErrorCallback();
		}

		return { ...newValidationState, [FORM_ERROR]: globalErrors ? globalErrors : errorMessage };
	} else {
		// generic error.
		if (genericErrorCallback) {
			genericErrorCallback();
		}

		if (errorMessage) {
			return { [FORM_ERROR]: errorMessage };
		} else {
			return {};
		}
	}
};

/**
 * Determines where a validation error occurred. Multiple sources can include the edit form, or any modals that also
 * include form fields that have validation such as redirects.
 * The returned value is treated as bitwise flags.
 */
export const getValidationErrorSource = (
	validationStateWithError: { [key: string]: any },
	axiosError: AxiosError,
): ValidationErrorSource => {
	// destructure out the `FORM_ERROR` key and only operate on the remainder
	const { ...validationState } = validationStateWithError;
	const validationKeys = Object.keys(validationState);

	let source = ValidationErrorSource.None;

	const redirectErrorIndex = validationKeys.indexOf('__redirects');

	if (redirectErrorIndex !== -1) {
		// a redirect error is present, flag it and remove it from the keys to check other sources
		source |= ValidationErrorSource.RedirectModal;
		validationKeys.splice(redirectErrorIndex, 1);
	}

	if (validationKeys.length > 0) {
		// if we still have validation errors at this point, the edit form was the culprit
		source |= ValidationErrorSource.EditForm;
	}

	if (axiosError && axiosError.response && axiosError.response.status !== 400) {
		// status 400 is used for validation error messaging, and if we got an error that isn't related to valdation
		// then the error source should be the edit form
		source |= ValidationErrorSource.EditForm;
	}

	return source;
};

/**
 * Get an html class or ID safe string from provided string.
 *
 * @function getHtmlSafeName
 */
export const getHtmlSafeName = (name: string = ''): string => {
	return name.replace(/[^a-z0-9]/g, (s) => {
		const c = s.charCodeAt(0);
		if (c === 32) {
			return '-';
		}
		if (c >= 65 && c <= 90) {
			return '_' + s.toLowerCase();
		}
		return '__' + ('000' + c.toString(16)).slice(-4);
	});
};

export interface Currency {
	/** The number of digits to always display to the right of the decimal point */
	readonly minPrecision: 0 | 1 | 2 | 3 | 4 | 5 | 6;

	/** If true, the currency symbol will occur before the price numbers */
	readonly isPrefix: boolean;

	/** The  */
	readonly currencySymbol: string;

	/** The unique three character ISO-4217 code for this currency. */
	readonly isoCode: string;
}

/** United States Dollars */
const USD: Currency = {
	isoCode: 'USD',
	currencySymbol: '$',
	isPrefix: true,
	minPrecision: 2,
};

/**
 * Formats a price for display.
 * @param price The price to format or NaN if the price is "not for sale"
 */
export const formatPrice = (price: number, currency: Currency = USD) => {
	const rawPriceFormat = (price: number) => {
		const priceString: string = price.toString();
		const decimalPointIndex: number = priceString.indexOf('.');
		const lastDigitIndex: number = priceString.length - 1;

		if (decimalPointIndex === -1) {
			return price.toFixed(currency.minPrecision);
		} else {
			const numberOfDecimalPlaces: number = lastDigitIndex - decimalPointIndex;

			if (numberOfDecimalPlaces <= currency.minPrecision) {
				return price.toFixed(currency.minPrecision);
			} else if (numberOfDecimalPlaces >= 7) {
				return price.toFixed(6);
			} else {
				return priceString;
			}
		}
	};

	if (price == null || isNaN(price)) {
		return 'N/A';
	}

	return currency.isPrefix
		? `${currency.currencySymbol}${rawPriceFormat(price)}`
		: `${rawPriceFormat(price)}${currency.currencySymbol}`;
};

/**
 * Returns the default site defined in the DB. Useful if there is no scoping available
 * and you're trying to link to some Global content, for example.
 */
export const getDefaultSite = (): AppSiteDto | undefined => {
	return AppState.sites.find((site) => site.isDefault);
};

/**
 * This interface closely resembles typescript enums
 */
export interface BaseEnum {
	[id: number]: string | number;
}

/**
 * This method gets the enum key from enum values
 */
export const getEnumKeyByValue = (value: string | number, type: BaseEnum) => {
	return Object.keys(type).find((t) => type[t] == value);
};

/**
 * Const value for encrypted settings
 */
export const EncryptedSettingsValue = '*****';

/**
 * Finds the token availabilities by field name
 */
export const findTokenAvailabilities = <T extends ReplacementTokenFields>(
	fieldName: string,
	replacementTokenFields?: T | null,
): TokenAvailability[] | null => {
	if (replacementTokenFields?.fields.some((f) => f.toLowerCase() === fieldName.toLowerCase())) {
		return replacementTokenFields.availabilities;
	}
	return null;
};

/**
 * Runs all react final form validators. If none of the validation results have any errors defined,
 * then the returned result will be undefined.
 */
export function validate(
	values: any,
	validators: ((values: any) => Record<string, unknown>)[],
): Record<string, unknown> | undefined {
	let result: Record<string, unknown> = {};

	for (const validator of validators) {
		const validationResult = validator(values);
		if (validationResult) {
			if (!result) {
				result = {};
			}
			merge(result, validationResult);
		}
	}

	for (const value of Object.values(flattenObject(result))) {
		if (typeof value === 'string' && value && value.length > 0) {
			// This means there is at least 1 error defined on the result.
			return result;
		}
	}

	return undefined;
}

/**
 * Flattens down a deeply nested object into a key/value pair object.
 */
export function flattenObject(obj: Record<string, unknown>, parentKey = '', result = {}): Record<string, unknown> {
	for (const key in obj) {
		if (obj.hasOwnProperty(key)) {
			const propKey = parentKey ? `${parentKey}.${key}` : key;
			const value = obj[key];

			if (typeof value === 'object' && value !== null && value !== undefined) {
				flattenObject(value as Record<string, unknown>, propKey, result);
			} else {
				result[propKey] = value;
			}
		}
	}

	return result;
}
